import { useEffect, useRef } from "react";

const states = [
  { name: "Connecticut", abbr: "CT" },
  { name: "Delaware", abbr: "DE" },
  { name: "Maryland", abbr: "MD" },
  { name: "Massachusetts", abbr: "MA" },
  { name: "New Jersey", abbr: "NJ" },
  { name: "New York", abbr: "NY" },
  { name: "Pennsylvania", abbr: "PA" },
  { name: "Rhode Island", abbr: "RI" },
  { name: "Ohio", abbr: "OH" },
];

interface ServiceAreaProps {
  onRequestLeads: () => void;
}

export default function ServiceArea({ onRequestLeads }: ServiceAreaProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".state-card");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "scale(1)";
              }, i * 60);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="service-area" className="py-24 md:py-32 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-green-600 text-xs font-bold uppercase tracking-widest mb-3 block">Coverage</span>
          <h2 className="text-3xl md:text-5xl font-bold text-black tracking-tight">Service Area</h2>
          <div className="w-16 h-1 bg-green-500 rounded-full mx-auto mt-4"></div>
          <p className="mt-5 text-gray-500 text-lg max-w-xl mx-auto">
            We currently serve the following states with exclusive, verified solar leads.
          </p>
        </div>

        {/* States grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
          {states.map((state) => (
            <div
              key={state.abbr}
              className="state-card bg-[#F7FAF7] border border-green-100 rounded-2xl p-5 flex flex-col items-center gap-2 hover:border-green-400 hover:bg-green-50 transition-all duration-200 cursor-default"
              style={{ opacity: 0, transform: "scale(0.92)", transition: "opacity 0.4s ease, transform 0.4s ease" }}
            >
              <div className="w-10 h-10 flex items-center justify-center bg-green-500 rounded-xl">
                <i className="ri-map-pin-2-fill text-white text-lg"></i>
              </div>
              <span className="text-2xl font-black text-green-600">{state.abbr}</span>
              <span className="text-gray-600 text-xs font-medium text-center">{state.name}</span>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="bg-[#0A1F0F] rounded-2xl p-7 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-green-500/20 rounded-xl flex-shrink-0">
              <i className="ri-global-line text-green-400 text-xl"></i>
            </div>
            <div>
              <p className="text-white font-bold text-lg">Expanding soon</p>
              <p className="text-gray-400 text-sm mt-1">
                More states coming in Q3 2025. Contact us to get on the waitlist for your region.
              </p>
            </div>
          </div>
          <button
            onClick={onRequestLeads}
            className="bg-green-500 hover:bg-green-400 text-black font-bold px-7 py-3.5 rounded-full text-sm transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
          >
            Check My Area
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
